import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { InfoTip } from "./InfoTip";

/**
 * Manual entry for requests and donations, opened as a modal from the
 * floating (+) button on the Queue tab.
 *
 * Until the Twitch/YouTube webhook ingestion exists, this is how the queue gets
 * fed — and it stays useful afterwards for admin corrections. It's also the
 * quickest way to watch matching work: add a free request, then donate as the
 * same user and watch it jump to paid. Stays open across a submit (fields
 * clear, a notice confirms) so a request-then-donation pair doesn't need
 * reopening the modal in between.
 */
export function AddPanel({
  channelId,
  onChanged,
  onClose,
}: {
  channelId: string;
  onChanged: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [platform, setPlatform] = useState("twitch");
  const [username, setUsername] = useState("");
  const [videoQuery, setVideoQuery] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  // Queue.tsx passes an inline onClose, so a fresh identity lands on every
  // parent re-render — a ref keeps the listener mount-once instead of
  // tearing down/re-adding on every unrelated re-render while open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const hasRequest = videoQuery.trim().length > 0;
  const hasDonation = Number(amount) > 0;
  const canSubmit = username.trim().length > 0 && (hasRequest || hasDonation);

  async function submit() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const user = username.trim();
      const messages: string[] = [];

      // Add the request first so a donation from the same user can match it.
      if (hasRequest) {
        const note = notes.trim();
        const { request } = await api.addRequest({
          channelId,
          videoQuery: videoQuery.trim(),
          platform,
          username: user,
          ...(note ? { notes: note } : {}),
        });
        messages.push(`Request added to ${request.section}.`);
      }

      if (hasDonation) {
        const res = await api.addDonation({
          channelId,
          platform,
          donorUsername: user,
          amountCents: Math.round(Number(amount) * 100),
        });
        messages.push(
          res.heldAsCredit
            ? `Donation held as credit for @${user}'s next request.`
            : `Donation matched → request is now ${res.matchedRequest?.section}.`,
        );
      }

      setNotice(messages.join(" "));
      setVideoQuery("");
      setAmount("");
      setNotes("");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const submitLabel =
    hasRequest && hasDonation
      ? "Add request + donation"
      : hasDonation
        ? "Add donation"
        : "Add request";

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Add to queue" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div className="label-row">
            <h2 style={{ marginBottom: 0 }}>Add to queue</h2>
            <InfoTip text="Fill in a video to add a request, an amount to add a donation, or both — a request and donation from the same user are matched automatically." />
          </div>
          <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <div className="field" style={{ flex: "0 0 120px" }}>
            <label htmlFor="platform">Platform</label>
            <select
              id="platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              <option value="twitch">Twitch</option>
              <option value="youtube">YouTube</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              value={username}
              placeholder="username"
              autoFocus
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <div className="field" style={{ flex: "2 1 260px" }}>
            <div className="label-row">
              <label htmlFor="videoQuery">YouTube URL or search <span className="muted">(request)</span></label>
              <InfoTip text="Paste a video link, or type a search (e.g. an artist + song title) to queue the top YouTube result automatically." />
            </div>
            <input
              id="videoQuery"
              value={videoQuery}
              placeholder="https://youtu.be/… or search terms"
              onChange={(e) => setVideoQuery(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: "0 1 130px" }}>
            <label htmlFor="amount">Amount ($) <span className="muted">(donation)</span></label>
            <input
              id="amount"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              placeholder="0.00"
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <div className="field">
            <label htmlFor="notes">Note <span className="muted">(request, optional)</span></label>
            <input
              id="notes"
              value={notes}
              maxLength={500}
              placeholder="e.g. play the acoustic version"
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <button className="btn primary row-action" onClick={submit} disabled={busy || !canSubmit}>
            {submitLabel}
          </button>
        </div>

        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
        {notice && (
          <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
            {notice}
          </p>
        )}
      </div>
    </div>
  );
}
